import { ref } from "vue";

// ── Module-level singleton ────────────────────────────────────────────────
// One shared counter for the whole app bundle.
const pending = ref(0);   // number of in-flight API requests
const navActive = ref(false); // true while a router navigation is in progress
const visible = ref(false);
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// ── startNav / endNav — called from router guards ─────────────────────────
// Show the overlay instantly when the user clicks a link, bypassing the
// debounce that protects against fast background pollers.
function startNav() {
  navActive.value = true;
  if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
  visible.value = true;
}

// Called from router.afterEach. We wait one macrotask (setTimeout 0) before
// deciding whether to hide, so the new component's setup() has time to
// register its own API calls. If calls are already in flight, the pending
// counter will keep the overlay alive until they complete.
function endNav() {
  navActive.value = false;
  setTimeout(() => {
    if (pending.value === 0) visible.value = false;
  }, 0);
}

// ── increment / decrement — called from Axios interceptors ───────────────
function increment() {
  pending.value++;
  // If overlay is already showing (e.g. router just started nav), no need
  // to debounce — just keep it visible.
  if (!visible.value && !debounceTimer) {
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      if (pending.value > 0) visible.value = true;
    }, 150);
  }
}

function decrement() {
  if (pending.value > 0) pending.value--;
  if (pending.value === 0 && !navActive.value) {
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    visible.value = false;
  }
}

export function useRequestLoader() {
  return { isLoading: visible, increment, decrement, startNav, endNav };
}
